import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dataDeletionService, DeletionResult, ParentalDeletionOptions } from '../services/DataDeletionService';

interface ParentalDataManagementProps {
  childUserId: string;
  childName: string;
  onClose: () => void;
}

const ParentalDataManagement: React.FC<ParentalDataManagementProps> = ({
  childUserId,
  childName,
  onClose
}) => {
  const { currentUser } = useAuth();
  const [dataSummary, setDataSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deletionResult, setDeletionResult] = useState<DeletionResult | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deletionOptions, setDeletionOptions] = useState<ParentalDeletionOptions>({
    deleteAllData: false,
    deleteLessons: false,
    deletePracticeData: false,
    deleteSocialData: false,
    keepAccount: true
  });

  useEffect(() => {
    loadDataSummary();
  }, [childUserId]);

  const loadDataSummary = async () => {
    try {
      setLoading(true);
      const summary = await dataDeletionService.getUserDataSummary(childUserId);
      setDataSummary(summary);
    } catch (error) {
      console.error('Error loading data summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletionOptionChange = (option: keyof ParentalDeletionOptions, value: boolean) => {
    setDeletionOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const handleDeleteData = async () => {
    try {
      setDeleting(true);
      const result = await dataDeletionService.deleteUserData(childUserId, deletionOptions);
      setDeletionResult(result);
      
      if (result.success) {
        // Reload data summary
        await loadDataSummary();
      }
    } catch (error) {
      console.error('Error deleting data:', error);
      setDeletionResult({
        success: false,
        deletedItems: {
          lessons: 0,
          practiceSessions: 0,
          feedEvents: 0,
          storageFiles: 0,
          userProfile: false
        },
        errors: [error instanceof Error ? error.message : 'Unknown error']
      });
    } finally {
      setDeleting(false);
      setShowConfirmDialog(false);
    }
  };

  const confirmDeletion = () => {
    setShowConfirmDialog(true);
  };

  const cancelDeletion = () => {
    setShowConfirmDialog(false);
    setDeletionResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Parental Data Management</h1>
                <p className="text-indigo-100 mt-1">Managing data for {childName}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-indigo-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Data Summary */}
            {dataSummary && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Data Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{dataSummary.lessons}</div>
                    <div className="text-sm text-blue-800">Lesson Recordings</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{dataSummary.practiceSessions}</div>
                    <div className="text-sm text-green-800">Practice Sessions</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">{dataSummary.feedEvents}</div>
                    <div className="text-sm text-yellow-800">Social Activities</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">{dataSummary.storageFiles}</div>
                    <div className="text-sm text-purple-800">Storage Files</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  Account created {dataSummary.accountAge} days ago
                </div>
              </div>
            )}

            {/* Deletion Options */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Deletion Options</h2>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={deletionOptions.deleteAllData}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDeletionOptions({
                          deleteAllData: checked,
                          deleteLessons: checked,
                          deletePracticeData: checked,
                          deleteSocialData: checked,
                          keepAccount: !checked
                        });
                      }}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Delete All Data</div>
                      <div className="text-sm text-gray-500">Remove all data and close the account</div>
                    </div>
                  </label>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={deletionOptions.deleteLessons}
                      onChange={(e) => handleDeletionOptionChange('deleteLessons', e.target.checked)}
                      disabled={deletionOptions.deleteAllData}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Delete Lesson Recordings</div>
                      <div className="text-sm text-gray-500">Remove all lesson recordings and summaries</div>
                    </div>
                  </label>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={deletionOptions.deletePracticeData}
                      onChange={(e) => handleDeletionOptionChange('deletePracticeData', e.target.checked)}
                      disabled={deletionOptions.deleteAllData}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Delete Practice Data</div>
                      <div className="text-sm text-gray-500">Remove practice sessions and progress tracking</div>
                    </div>
                  </label>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={deletionOptions.deleteSocialData}
                      onChange={(e) => handleDeletionOptionChange('deleteSocialData', e.target.checked)}
                      disabled={deletionOptions.deleteAllData}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Delete Social Data</div>
                      <div className="text-sm text-gray-500">Remove social activities and feed events</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={confirmDeletion}
                disabled={deleting || (!deletionOptions.deleteAllData && !deletionOptions.deleteLessons && !deletionOptions.deletePracticeData && !deletionOptions.deleteSocialData)}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete Selected Data'}
              </button>
              <button
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>

            {/* Deletion Result */}
            {deletionResult && (
              <div className={`mt-6 p-4 rounded-lg ${
                deletionResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <h3 className={`font-medium ${
                  deletionResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {deletionResult.success ? 'Deletion Completed' : 'Deletion Failed'}
                </h3>
                <div className="mt-2 text-sm">
                  {deletionResult.success ? (
                    <div className="text-green-700">
                      <p>Successfully deleted:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>{deletionResult.deletedItems.lessons} lesson recordings</li>
                        <li>{deletionResult.deletedItems.practiceSessions} practice sessions</li>
                        <li>{deletionResult.deletedItems.feedEvents} social activities</li>
                        <li>{deletionResult.deletedItems.storageFiles} storage files</li>
                        {deletionResult.deletedItems.userProfile && <li>User profile</li>}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-red-700">
                      <p>Errors occurred during deletion:</p>
                      <ul className="list-disc list-inside mt-1">
                        {deletionResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Data Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the selected data? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDeleteData}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button
                onClick={cancelDeletion}
                disabled={deleting}
                className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentalDataManagement;
